import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { WhatsAppShareButton } from "../components/WhatsAppWidget";
import ProductCard from "../components/ProductCard";
import { Plus, Minus, ShoppingCart, ArrowLeft, Storefront, WarningCircle } from "@phosphor-icons/react";
import { formatGBP } from "../lib/format";
import { toast } from "sonner";

export default function ProductDetail() {
  const { product_id } = useParams();
  const { add, items, updateQty } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);

  const inCart = items.find((i) => i.product_id === product_id);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/products/${product_id}`);
        setProduct(data);
        const { data: rel } = await api.get(`/products/${product_id}/related`);
        setRelated(rel);
      } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[ProductDetail] non-fatal error:", err); }
      setLoading(false);
    })();
    setQty(1);
  }, [product_id]);

  if (loading || !product) return <div className="min-h-screen flex items-center justify-center text-sm text-[#5C5C54]">Loading...</div>;

  const out = product.stock_quantity <= 0;
  const low = !out && product.stock_quantity <= (product.low_stock_threshold || 5);

  const handleAdd = () => {
    add(product, qty);
    toast.success(`${qty} × ${product.name} added to cart`);
  };

  const shareMsg = `Check out ${product.name} on AfriPantry — ${formatGBP(product.price)}. ${window.location.href}`;

  return (
    <div className="pt-20 pb-24 max-w-7xl mx-auto px-4 md:px-8 lg:px-12" data-testid="product-detail-page">
      <Link to="/products" className="inline-flex items-center gap-2 text-xs font-bold text-[#5C5C54] hover:text-[#A94438] mb-6">
        <ArrowLeft size={14} weight="bold" /> Back to products
      </Link>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        <div className="rounded-3xl overflow-hidden bg-[#F2EFE9] aspect-square">
          <img src={product.images?.[0]} alt={product.name} onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/37099829/pexels-photo-37099829.jpeg"; }} className="w-full h-full object-cover" />
        </div>

        <div>
          <Link to={`/vendors/${product.vendor_name?.toLowerCase().replace(/\s/g, "-")}`} className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase text-[#A94438] mb-3 hover:underline">
            <Storefront size={14} weight="duotone" /> {product.vendor_name}
          </Link>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-3">{product.name}</h1>
          <p className="font-heading text-2xl md:text-3xl font-black text-[#A94438] mb-4">{formatGBP(product.price)}</p>

          <p className="text-sm md:text-base text-[#5C5C54] leading-relaxed mb-5">{product.description}</p>

          {product.cultural_tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {product.cultural_tags.map((t) => <span key={t} className="chip">{t}</span>)}
            </div>
          )}

          {low && (
            <p className="chip-warning mb-4 inline-flex" data-testid="product-low-stock">
              <WarningCircle size={12} weight="fill" /> Only {product.stock_quantity} left in stock
            </p>
          )}
          {out && (
            <p className="text-sm font-bold text-[#A94438] mb-4" data-testid="product-out-of-stock">Out of stock. We'll restock soon.</p>
          )}

          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-3 bg-white border-2 border-[#E5E2D9] rounded-full px-3 py-2">
              <button
                data-testid="detail-qty-decrement"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-8 h-8 rounded-full bg-[#F2EFE9] flex items-center justify-center hover:bg-[#E5E2D9]"
              >
                <Minus size={14} weight="bold" />
              </button>
              <span className="font-bold w-6 text-center" data-testid="detail-qty-value">{qty}</span>
              <button
                data-testid="detail-qty-increment"
                onClick={() => setQty(Math.min(product.stock_quantity, qty + 1))}
                disabled={qty >= product.stock_quantity}
                className="w-8 h-8 rounded-full bg-[#1A1A18] text-white flex items-center justify-center hover:bg-[#A94438] disabled:opacity-40"
              >
                <Plus size={14} weight="bold" />
              </button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              onClick={handleAdd}
              disabled={out}
              className="btn-primary gap-2 flex-1"
            >
              <ShoppingCart size={18} weight="duotone" /> Add to cart
            </button>
          </div>

          {inCart && (
            <p className="text-xs text-[#2B5329] font-semibold mb-4">
              You have {inCart.quantity} in cart. <Link to="/cart" className="underline">View cart →</Link>
            </p>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <WhatsAppShareButton message={shareMsg} label="Share on WhatsApp" testid="product-share-whatsapp" />
          </div>

          <div className="mt-8 pt-6 border-t border-[#E5E2D9] grid grid-cols-2 gap-4 text-xs">
            <div>
              <p className="font-bold text-[#1A1A18] mb-1">Category</p>
              <p className="text-[#5C5C54]">{product.category}</p>
            </div>
            <div>
              <p className="font-bold text-[#1A1A18] mb-1">In stock</p>
              <p className="text-[#5C5C54]">{product.stock_quantity} units</p>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16" data-testid="related-products">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-6">People also bought</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((r) => <ProductCard key={r.product_id} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
